import { createContext, useContext, useEffect, useRef } from "react";
export const ModuleHostContext = createContext(null);
const useHost = (hook) => {
    const host = useContext(ModuleHostContext);
    if (!host)
        throw new Error(`${hook} must be used inside a React ArtisModule rendered by <ArtisModuleHost>`);
    return host;
};
const useLatest = (value) => {
    const ref = useRef(value);
    useEffect(() => {
        ref.current = value;
    });
    return ref;
};
/**
 * The Pane's {@link ModuleStorage} for `scope`, pre-scoped to this module's registered
 * type and the current user. Referentially stable for the Pane's lifetime, so it is
 * safe in dependency arrays. See {@link ModuleStorage} for the storage contract.
 */
export const useModuleStorage = (scope) => {
    const { ctx } = useHost("useModuleStorage");
    return ctx.storage(scope);
};
/** Runs once when the module is loaded into its Pane (on mount). */
export const useModuleLoad = (cb) => {
    const { paneId, elementRef } = useHost("useModuleLoad");
    const cbRef = useLatest(cb);
    // biome-ignore lint/correctness/useExhaustiveDependencies: load fires once, on mount
    useEffect(() => {
        const element = elementRef.current;
        if (element)
            cbRef.current({ id: paneId, element });
    }, []);
};
/** Runs when the module becomes visible: on mount, and again on any later re-show. */
export const useModuleShow = (cb) => {
    const { paneId, elementRef, visible } = useHost("useModuleShow");
    const cbRef = useLatest(cb);
    useEffect(() => {
        const element = elementRef.current;
        if (visible && element)
            cbRef.current({ id: paneId, element });
    }, [visible, paneId, elementRef, cbRef]);
};
/** Runs whenever the Pane resizes, with the new size. */
export const useModuleResize = (cb) => {
    const { paneId, elementRef, onResize } = useHost("useModuleResize");
    const cbRef = useLatest(cb);
    useEffect(() => onResize((size) => {
        const element = elementRef.current;
        if (element)
            cbRef.current({ id: paneId, element }, size);
    }), [onResize, paneId, elementRef, cbRef]);
};
/** Runs when the module becomes hidden but stays alive (e.g. its tab is deactivated). Not on unload. */
export const useModuleHide = (cb) => {
    const { paneId, elementRef, visible } = useHost("useModuleHide");
    const cbRef = useLatest(cb);
    const mounted = useRef(false);
    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }
        const element = elementRef.current;
        if (!visible && element)
            cbRef.current({ id: paneId, element });
    }, [visible, paneId, elementRef, cbRef]);
};
/** Runs once when the module is unloaded from its Pane (on unmount). */
export const useModuleUnload = (cb) => {
    const { paneId, elementRef } = useHost("useModuleUnload");
    const cbRef = useLatest(cb);
    // biome-ignore lint/correctness/useExhaustiveDependencies: unload fires once, on unmount
    useEffect(() => () => {
        const element = elementRef.current;
        if (element)
            cbRef.current({ id: paneId, element });
    }, []);
};
//# sourceMappingURL=moduleLifecycle.js.map